import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { POSITION } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { findNearestMatch, getChildNodes, KeyboardInput, setHTMLElementContent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Action } from "@cervval.js/common-action/src/action.model";
import { LOG_SERVICE, LogService, VALIDATION_SERVICE, ValidationService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { EDITED_DATA_IDS } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { asAnyObject, getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { isNumeric } from "@cervval.js/common/src/tools/number.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CjsEditor, ErrorMessageDisplayMode } from "../../editors/editor";
import { ShowPopupReturn } from "../showPopup/showPopup";
import { CjsSmartTable, SINGLE_COLUMN_UID, SmartTableCell, SmartTableValueEditOrigin } from "./smartTable";

/**
 * State of smartTable edition
 * read: readonly content
 * edit: content is currently editable => editor is show instead of cell content
 */
export type SmartTableEditState = "read" | "edit";
export const DEFAULT_SMART_TABLE_EDIT = "default-cjs-smart-table-edit";

/** Editors with these functions can override key listener behavior */
export const SHOULD_FINISH_ON_ESCAPE_FUNCTION = "shouldSmartTableFinishValueEditionOnEscape";
export const SHOULD_FINISH_ON_ENTER_FUNCTION = "shouldSmartTableFinishValueEditionOnEnter";

export const EDITION_CANCELLED_EVENT = "editionCancelled";
export interface SmartTableEditionContext<ItemData> {

    /** Cell in edition */
    cell: SmartTableCell<ItemData> | undefined;

    /**
     * Content of cell currently edited
     * "Cache" initial cell content during edition
     */
    content: ChildNode[] | undefined;

    /**
     * Editor of cell
     */
    editor: CjsEditor<anyReallyAny> | undefined;

    /**
     * Popup of editor if exist
     */
    popup?: ShowPopupReturn;
}

/**
 * Helper used to edit content of a cell
 */
export class SmartTableEditionHelper<ItemData> {

    /** Focus cell */
    protected _focusCell?: SmartTableCell<ItemData>;

    /** Current state of edition of smartTable */
    public editState: SmartTableEditState = "read";

    @Injected(LOG_SERVICE)
    protected readonly _log!: LogService;

    @Injected(VALIDATION_SERVICE)
    protected readonly _validation!: ValidationService;

    /** Current context of edition */
    public context: SmartTableEditionContext<ItemData> = {
        cell: undefined,
        content: undefined,
        editor: undefined,
        popup: undefined
    };

    /** Wether or not there's a validation error, in which case some features are blocked */
    public isHandlingValidationError: boolean = false;

    constructor(public smartTable: CjsSmartTable<ItemData>) {
        this._initListeners();
    }

    /**
     * If provided cell corresponds to the current focus cell it is registered as the new focus cell
     * @returns true if cell is the focus cell
     */
    public refreshFocusCellIfSame(cell: SmartTableCell<ItemData>): boolean {
        if (this._focusCell && this._focusCell !== cell && this._focusCell.column.uid === cell.column.uid && this._focusCell.row.uid === cell.row.uid) {
            // cell is the same as focus cell
            this.setFocusCell(cell);
            return true;
        } else if (this._focusCell === cell) {
            // cell is the focus cell but its html may have been overwritten
            this._focusCell.html?.classList.add("focus");
            return true;
        }
        return false;
    }

    /**
     * record focus cell and add css class
     */
    public setFocusCell(cell: SmartTableCell<ItemData> | undefined, origin?: "click" | "key"): void {
        if (this._focusCell !== cell) {
            if (this._focusCell) {
                this._focusCell.html?.classList.remove("focus");
            }

            if (cell && !this.smartTable.options.isCellEditable?.(cell)) {
                cell = undefined;
            }

            this.smartTable.focusChangeEvent.emit({
                oldCell: this._focusCell,
                newCell: cell
            });

            this._focusCell = cell;
            if (this._focusCell) {
                if (origin === "key" && !cell?.column.selectCallbackDisabled) {
                    this.smartTable.dispatchRowSelected(this._focusCell, { origin: "key" }, { keepSelection: false });
                }
                this._focusCell.html?.classList.add("focus");
            }

        }
    }


    /** Get current cell focused */
    public getFocusCell(): SmartTableCell<ItemData> | undefined {
        return this._focusCell;
    }

    /**
     * Edit cell value if needed or close edition mode
     * @returns the editor
     */
    public editValueIfNeeded(cellClicked: SmartTableCell<ItemData>, origin: { click?: "simple" | "double", keyEvent?: KeyboardEvent }): HTMLElement | undefined {

        if (!this.smartTable.options.isCellEditable) { return undefined; }

        const nonEditableColumns = [SINGLE_COLUMN_UID];
        // Return if column is selector
        if (nonEditableColumns.includes(cellClicked.column.uid)) {
            return undefined;
        }

        // Is cell is editable ?
        const cellIsEditable = this.smartTable.options.isCellEditable?.(cellClicked);

        if (!this.isEditState() && cellClicked.html && cellIsEditable && (origin.click === "double" || origin.keyEvent)) {// Only edit if double click or key press
            this.editState = "edit";

            // Get editor from configuration
            const editor = this.smartTable.getCellEditor(cellClicked);
            if (!editor) {
                // cell is not editable
                this.editState = "read";
                return undefined;
            }

            editor.classList.add(DEFAULT_SMART_TABLE_EDIT);
            // First, set editor as content of cell (some editors required to be added in DOM before setting value)
            this._setCellContent(cellClicked, editor);
            this._refreshCellEditionClasses(cellClicked);

            // Set value in editor
            if (this.smartTable.options.setCellEditorValue) {
                this.smartTable.options.setCellEditorValue(cellClicked, editor, cellClicked.value);
            } else {
                editor.setValue(cellClicked.value);
            }

            // Add listeners
            editor.addEventListener("keyup", this._onFinishValueEditionKeyPressed);// finishValueEdition on Enter and Escape
            editor.addEventListener(EDITION_CANCELLED_EVENT, () => {
                this._finishValueEdition(false);
            });

            // Stop event propagation to avoid event bubbling to current row (popup is append in row, so click will trigger a "click" event on row without this)
            editor.addEventListener("click", e => e.stopPropagation());

            // Update context, save cell content
            this.context = {
                content: cellClicked.html ? getChildNodes(cellClicked.html) : undefined,
                cell: cellClicked,
                editor
            };

            this._focusAndSelect(editor);
            if (origin.keyEvent) {
                this._dispatchKeyEvent(editor, origin.keyEvent);
            }

            return editor;

        } else if (this.context.cell !== cellClicked && this.isEditState()) {// When user click on other cell than currently edited
            // Execute value edition
            this._finishValueEdition();
            if (!this.isHandlingValidationError) {

                // Remove listener in editor if there there isn't any error to manage
                const editor = this.context.editor;
                if (editor) {
                    editor.removeEventListener("keyup", this._onFinishValueEditionKeyPressed);
                }
            }
        }

        return undefined;
    }


    /**
     * @returns true is table is in edit mode
     */
    public isEditState(): boolean {
        return this.editState === "edit";
    }

    /** Clear edition context */
    public clearContext(): void {
        this.context = {
            cell: undefined,
            content: undefined,
            editor: undefined
        };
    }

    /**
     * Behavior to apply to cell of entering in edition mode
     * 
     * @param editor to edit value
     * @param cell in edition
     */
    public handleCellInEdition(editor: CjsEditor<anyReallyAny>, cell: SmartTableCell<ItemData>): void {
        editor.tryEdit(this._finishValueEdition.bind(this));

        // ? Note: cell is unused by default, but can be usefull for other implementations
    }

    /**
     * Apply cell validation value check edited cell
     *
     * @param {SmartTableCell<ItemData>} cell the edited cell
     * @param {CjsEditor<anyReallyAny>} editor edited cell editor, on which value validation will be run and error displayed
     * @returns {boolean}  whether the new value respects validation or not
     * @memberof SmartTableEditionHelper
     */
    public validateCellEdition(cell: SmartTableCell<ItemData>, editor: CjsEditor<anyReallyAny> | undefined): boolean {
        const data = cell.rowItemData;
        const cellPropertyConfig = cell.column.propertyConfig;
        if (data == null) {
            this._log?.error("can't validate cell item data if there is no data");
            return true;
        }
        if (cellPropertyConfig === undefined) {
            this._log?.error("Can't validate cell item data without proper property configuration");
            return true;
        }

        const allProps: Record<string, PropertyConfig> = {};
        const propsValues: Record<string, unknown> = {};
        for (const col of this.smartTable.options.columns) {
            if (col.propertyConfig != null) {
                allProps[col.propertyConfig.propertyName] = col.propertyConfig;
                propsValues[col.propertyConfig.propertyName] = this.smartTable.buildCell(cell.row, col, undefined, { doNotRegisterCellInCache: true })?.value;
            }
        }
        propsValues[cellPropertyConfig.propertyName] = editor?.getValue();

        /** used for complex validation */
        const id = getProp<number>(data, "id");
        if (id != null && isNumeric(id)) {
            propsValues[EDITED_DATA_IDS] = id;
        }

        const errors = this._validation.validate(allProps, propsValues, [cellPropertyConfig.propertyName]);

        editor?.setValidationError(errors[cellPropertyConfig.propertyName], ErrorMessageDisplayMode.TOOLTIP);

        // Test if there are errors in Record
        return Object.keys(errors).length === 0;
    }

    /** 
     * Valid value edition,
     * close editor, dispatch value change event, 
     * and reset content of table
     */
    private _finishValueEdition(updateValue = true, p?: { forcedValue: { value: anyReallyAny } }): void {
        const cell = this.context.cell;

        if (!cell) { return; }

        const cellHtml = cell.html;
        const editor = this.context.editor;

        if (cellHtml && (editor || p?.forcedValue != null) && this.isEditState()) {
            this.isHandlingValidationError = !this.validateCellEdition(cell, editor);
            if (this.isHandlingValidationError) { return; }// as validation was a failure, all finish edition process is skiped


            this.editState = "read";

            setHTMLElementContent(cellHtml, ... this.context.content ?? []);
            this._refreshCellEditionClasses(cell);

            if (this.context.popup) {
                // Remove popup if still opened, necessary with editor who valid their value by "enter" key pressed
                this.context.popup.removePopup("cancel");
            }

            this._emitCellEditionFinishEvent(updateValue, p);
            this.clearContext();

            // give back focus to table
            this.smartTable.focus();
        }
    }

    /**
     * Some editors handle Escape and this helper should not handle Escape for them
     */
    protected _shouldFinishValueEditionOnEscape(editor: CjsEditor<anyReallyAny> | undefined): boolean {
        if (!editor) { return true; }
        const shouldFinishOnEscapeFunc = getProp(editor, SHOULD_FINISH_ON_ESCAPE_FUNCTION);
        if (typeof shouldFinishOnEscapeFunc === "function") {
            return shouldFinishOnEscapeFunc() === true;
        }
        return true;
    }

    /**
     * Some editors handle Enter and this helper should not handle Enter for them
     */
    protected _shouldFinishValueEditionOnEnter(editor: CjsEditor<anyReallyAny> | undefined): boolean {
        if (!editor) { return true; }
        const shouldFinishOnEnterFunc = getProp(editor, SHOULD_FINISH_ON_ENTER_FUNCTION);
        if (typeof shouldFinishOnEnterFunc === "function") {
            return shouldFinishOnEnterFunc() === true;
        }
        return true;
    }

    /** On keyboard key is pressed when editor is focused */
    private _onFinishValueEditionKeyPressed: EventListenerObject = {
        handleEvent: (e: KeyboardEvent): void => {
            if (e.key === KeyboardInput.ESCAPE && this.isEditState() && this._shouldFinishValueEditionOnEscape(this.context?.editor)) {
                this._finishValueEdition(false);
            }
            // Do not finish edition when both shift and enter keys are pressed
            if (!e.shiftKey && e.key === KeyboardInput.ENTER && this.isEditState() && this._shouldFinishValueEditionOnEnter(this.context?.editor)) {
                this._finishValueEdition();
            }
        }
    };

    /** Emit event when cell edition is finished (applied or cancelled) */
    private _emitCellEditionFinishEvent(updateValue = true, p?: { forcedValue: { value: anyReallyAny } }): void {
        const cell = this.context.cell;
        const editor = this.context.editor;

        if (cell && (editor || p != null)) {
            let newValue = updateValue ? editor?.getValue() : cell.value;// Return oldValue if update is cancelled
            if (p?.forcedValue) {// Override value
                newValue = p.forcedValue.value;
            }

            this.smartTable.cellEditionFinishEvent.emit({
                cell,
                newValue
            });
        }
    }

    /** Refresh classes of cell "<div>" parent of editor, for correct display */
    private _refreshCellEditionClasses(cell: SmartTableCell<ItemData>): void {
        if (cell.html) {
            // this is needed for EditorWithButtons to work properly
            toggleClass(cell.html, this.isEditState(), POSITION.RELATIVE);
        }
    }

    /** Init help listeners */
    private _initListeners(): void {

        // enable listen to keydown
        this.smartTable.tabIndex = 0;

        // Add keyboard listener
        this.smartTable.addEventListener("keydown", this._onKeyboardDown.bind(this));

        // When cell editedValue event is dispatched, reset default content
        this.smartTable.cellEditionFinishEvent.on(async (e) => {

            const cell = e.detail.cell;

            const newValue = e.detail.newValue;
            const oldValue = cell.value;

            // Apply new value if given
            if (oldValue !== newValue && cell.row.data) {

                // Dispatch event only if value has changed
                if (this.smartTable.options.encapsulateEditsInAction) {
                    await this.smartTable.options.encapsulateEditsInAction((action) => {
                        return this._setCellValue(cell, newValue, "editor", action);
                    }).catch(this._log.error);
                } else {
                    await this._setCellValue(cell, newValue, "editor");
                }
                // Update value in cell
                cell.value = this.smartTable.getCellValue(cell);
            }

            const td = cell.html;
            if (td) {
                this.smartTable.renderTdCellElement(cell, undefined);
                if (cell.html) {
                    td.after(cell.html);// Add new render after old one
                    td.remove();// Remove old one
                }
            }
        });
    }

    /** Set content of cell */
    private _setCellContent(cell: SmartTableCell<ItemData>, editor: CjsEditor<anyReallyAny>): void {
        if (cell.html) {
            // First, set editor in cell, to retrieve size 
            setHTMLElementContent(cell.html, editor);

            // Handle in edition behavior
            this.handleCellInEdition(editor, cell);
        }
    }

    /**
     * Emits cellEditedValueEvent
     * Set new value of cell within row data (the cell may not be rendered in the DOM and won't be rendered by this function)
     */
    protected async _setCellValue(cell: SmartTableCell<ItemData>, newValue: anyReallyAny, origin: SmartTableValueEditOrigin, action?: Action): Promise<void> {

        if (cell.column.setCellValue) {
            await cell.column.setCellValue(cell, newValue, origin, action);
        } else {

            this.smartTable.cellEditedValueEvent.emit({ cell, newValue, origin, action });

            // Update value in data
            // TODO use "BGS new DataModel" once available
            if (!action && cell.rowItemData && cell.column.propertyConfig?.propertyName) {
                // if we have an action, the data is supposed to be updated in cellEditedValueEvent callback
                // otherwise we update value here
                setProp(cell.rowItemData, cell.column.propertyConfig.propertyName, newValue);
            }
        }
    }

    /**
     * Give focus to editor, select text
     */
    protected _focusAndSelect(editor: HTMLElement | undefined): void {
        if (editor == null) { return; }

        // give focus to editor
        editor.focus();

        // select text within editor
        const selectFunc = getProp(editor, "select");
        if (typeof selectFunc === "function") {
            selectFunc.bind(editor)();
        }
    }

    /**
     * Dispatch key event
     */
    protected _dispatchKeyEvent(editor: HTMLElement | undefined, reDispatchKeyEvent: KeyboardEvent | undefined): void {
        if (editor == null) { return; }

        // dispatch key down event to editor
        if (reDispatchKeyEvent) {
            const event = new KeyboardEvent(reDispatchKeyEvent.type, reDispatchKeyEvent);
            if (typeof asAnyObject(editor)["dispatchEventOnInput"] === "function") {
                asAnyObject(editor)["dispatchEventOnInput"](event);
            } else {
                editor?.dispatchEvent(event);
            }
        }
    }

    /**
     * Copy selected values to clipboard
     */
    protected _copyToClipboard(): void {
        if (!this._focusCell) { return; }
        // TODO copy with multiple lines and columns handling

        const value = this.smartTable.getCellValue(this._focusCell, undefined);
        const displayStringValue = String(value);// TODO get String display value from editor or type
        const stringRawValue = String(value);// TODO get String raw value from editor or type (and use JSON.stringify for complex data)

        const data = [
            new ClipboardItem({
                "text/plain": new Blob([stringRawValue], { type: "text/plain" }),
                "text/html": new Blob([displayStringValue], { type: "text/html" })
            })
        ];
        navigator.clipboard.write(data).catch(console.error);
    }

    /**
     * Execute paste on a single cell using its editor (the cell may not be rendered in the DOM)
     */
    protected _executeSingleCellPaste(cell: SmartTableCell<ItemData>, newValue: anyReallyAny, action?: Action): void {
        // TODO use editor to ensure cell is editable and data is validated
        if (this.smartTable.options.isCellEditable?.(cell)) {
            this._setCellValue(cell, newValue, "paste", action);
        }
    }

    /**
     * Execute paste on table cells using their respective editor
     */
    protected _executePaste(fromCell: SmartTableCell<ItemData>, cells: string[][], action?: Action): void {
        if (!fromCell || !Array.isArray(cells)) { return; }
        const cell = fromCell;
        const displayableRows = this.smartTable.displayableRows;
        const fromRowIndex = displayableRows.indexOf(cell.row);
        const tableColumns = this.smartTable.options.columns;
        const fromColumnIndex = tableColumns.indexOf(cell.column);
        if (fromRowIndex < 0 || fromColumnIndex < 0) {
            // cell not found in table => we process paste on provided cell
            const newValue = cells[0][0];
            this._executeSingleCellPaste(cell, newValue, action);
        } else {
            for (let line = 0; line < cells.length; line++) {
                const lineCells = cells[line];
                if (Array.isArray(lineCells)) {
                    for (let col = 0; col < lineCells.length; col++) {
                        const cellValue = lineCells[col];
                        const tableColumn = tableColumns[fromColumnIndex + col];
                        const tableRow = displayableRows[fromRowIndex + line];
                        if (tableColumn != null && tableRow != null) {
                            const pasteCell = this.smartTable.buildCell(tableRow, tableColumn, undefined, { doNotRegisterCellInCache: true });
                            this._executeSingleCellPaste(pasteCell, cellValue, action);
                        }
                    }
                }
            }
        }

        this.smartTable.render("paste");// TODO render only updated cells/rows ?
    }

    /**
     * Paste clipboard values into editable cells
     */
    protected _pasteFromClipboard(): void {
        if (!this._focusCell) { return; }
        const focusCell = this._focusCell;
        // TODO handle text areas and tinyMCE => allow multiple lines
        navigator.clipboard.readText().then(data => {

            // TODO parse with quote handling (use csvparse node module ?)
            const cells: string[][] = [];
            const lines = String(data).replace(new RegExp("\\r", "g"), "").split("\n");

            // If last element of the array is empty, delete it (will prevent the creation of an additionnal empty cell)
            if (lines.length > 0 && lines[lines.length - 1] === "") { lines.pop(); }

            for (let l = 0; l < lines.length; l++) {
                const lineCells: string[] = [];
                cells[l] = lineCells;
                const cols = (lines[l] ?? "").split("\t");
                for (let c = 0; c < cols.length; c++) {
                    const cellValue = cols[c];
                    lineCells[c] = cellValue;
                }
            }
            if (this.smartTable.options.encapsulateEditsInAction) {
                return this.smartTable.options.encapsulateEditsInAction((action) => {
                    this._executePaste(focusCell, cells, action);
                });
            } else {
                this._executePaste(focusCell, cells);
            }
            return null;
        }).catch(this._log.error);

    }


    /** On keyboard key is pressed down event */
    protected _onKeyboardDown(e: KeyboardEvent): void {

        // Try to move cell first
        this._moveFocusedCellWithKeyboard(e);

        // Handle copy/paster, simple key press (ex: "s"/"l", not "arrowUp"/"enter"), backspace and delete
        this._updateEditedValueWithKeyboard(e);
    }

    /** Update the current edited value with keyboard key pressed event */
    private _updateEditedValueWithKeyboard(evt: KeyboardEvent): void {
        if (this.isEditState() || !this._focusCell) { return; }
        if (evt.ctrlKey) {
            // handle copy/paste, we don't use oncopy and onpaste events because they don't work on the table, they work on input components
            if (evt.key === KeyboardInput.C && !this.smartTable.options.overrideCopy) {
                this._copyToClipboard();
            } else if (evt.key === KeyboardInput.V || evt.key === KeyboardInput.V_CAPS) {
                this._pasteFromClipboard();
            }
        } else {
            // if user pressed a single char key (ie not a special key such as ArrowUp), and we have a focus cell not in edition mode, we trigger an edition
            if (evt.key.length === 1 || evt.key === KeyboardInput.BACKSPACE) {
                this.editValueIfNeeded(this._focusCell, { keyEvent: evt });
            } else if (evt.key === KeyboardInput.DELETE) {
                this.editState = "edit";
                this.context.cell = this._focusCell;
                this._finishValueEdition(true, { forcedValue: { value: undefined } });
            }
        }
    }

    /** Keys used to move focusCell with keyboard */
    private _allowedNavigationKeys: string[] = [KeyboardInput.ARROW_UP, KeyboardInput.ARROW_DOWN, KeyboardInput.ARROW_RIGHT, KeyboardInput.ARROW_LEFT];

    /** Move focusCell if an arrow key is pressed by user */
    private _moveFocusedCellWithKeyboard(evt: KeyboardEvent): void {

        // Handle other keys than arrow keys, return
        if (!this._allowedNavigationKeys.includes(evt.key) || this.editState === "edit") { return; }

        // Finish edition of value if a value is currently edited
        this._finishValueEdition();

        const columns = this.smartTable.options.columns;

        // Handle no cell already focused, select first cell
        if (!this._focusCell) {

            // Find first editable cell, loop into all columns
            for (const row of this.smartTable.displayedRows) {
                for (const column of columns) {
                    const firstRowCell = this.smartTable.getCellByRowAndColumn(row.uid, column.uid);
                    // If cell is editable, set this cell
                    if (firstRowCell && this.smartTable.options.isCellEditable?.(firstRowCell)) {
                        this.setFocusCell(firstRowCell, "key");
                        break;
                    }
                }
            }
        } else {// Select next cell
            let newCell: SmartTableCell<ItemData> | undefined = undefined;

            // Handle than cell is outer bounds
            if (evt.key === KeyboardInput.ARROW_UP || evt.key === KeyboardInput.ARROW_DOWN) {
                this._handleCellFocusScroll(evt.key);
            }

            const cells = this.smartTable.cellsByRowAndColumn;

            // Switch on key input
            switch (evt.key) {

                case KeyboardInput.ARROW_UP: {// Move top
                    newCell = this._findClosestEditableCell(cells, "up");
                    break;
                }
                case KeyboardInput.ARROW_DOWN: {// Move down
                    newCell = this._findClosestEditableCell(cells, "down");
                    break;
                }
                case KeyboardInput.ARROW_RIGHT: {// Move right
                    newCell = this._findClosestEditableCell(cells, "right");
                    break;
                }
                case KeyboardInput.ARROW_LEFT: {// Move left
                    newCell = this._findClosestEditableCell(cells, "left");
                    break;
                }
                default:
                    break;
            }
            if (newCell) {
                this.setFocusCell(newCell, "key");
            }

        }
    }


    /**
     * Find closest <by direction> cell editable of current focused cell
     */
    private _findClosestEditableCell(cells: Record<number, Record<string, SmartTableCell<ItemData>>>, direction: "up" | "down" | "left" | "right"): SmartTableCell<ItemData> | undefined {

        if (this._focusCell) {

            // Cells sorted to check editable state of cells
            let directionCells: SmartTableCell<ItemData>[] = [];

            // Index in columnOrRowCell of current focusCell
            let focusCellIndex = -1;

            // List of cell in column or row, depends on the direction of action
            let columnOrRowCells: SmartTableCell<ItemData>[] = [];

            if (direction === "up" || direction === "down") {
                // Get all cells in column
                const colUid = this._focusCell?.column.uid;
                for (const rowUid in cells) {
                    const colCell = cells[rowUid]?.[colUid];
                    if (colCell) {
                        if (rowUid === this._focusCell.row.uid) {
                            focusCellIndex = columnOrRowCells.length;
                        }
                        columnOrRowCells.push(colCell);
                    }
                }
            } else {
                // Get all cells in row
                const rowCells = cells[Number(this._focusCell.row.uid)];
                if (rowCells) {
                    columnOrRowCells = Object.values(rowCells);
                }
                focusCellIndex = columnOrRowCells.findIndex(cell => cell.column.uid === this._focusCell?.column.uid);
            }


            if (focusCellIndex < 0) {
                if (direction === "up") {
                    // coming from first row
                    focusCellIndex = columnOrRowCells.length;
                } else if (direction === "down") {
                    // coming from last row
                    focusCellIndex = -1;
                } else {
                    return undefined;
                }
            }

            // Split array of cells with index of focusCell
            const beforeCells = columnOrRowCells.slice(0, focusCellIndex);
            const afterCells = columnOrRowCells.slice(focusCellIndex);

            // Set focusCell as first index in list of cell to check editable state
            directionCells = [...afterCells, ...beforeCells];

            // Handle negative moves
            if (direction === "up" || direction === "left") {
                // Add first cell in first of array to jump over it
                const firstCell = directionCells[0];
                directionCells = [firstCell, ...directionCells.reverse()];
            }

            // Start at "1" to avoid current focusCell
            for (let i = 1; i < directionCells.length; i++) {
                const cell = directionCells[i];
                // If cell is editable, set this cell
                if (cell && this.smartTable.options.isCellEditable?.(cell)) {
                    return cell;
                }
            }
        }

        // No cell found or editable in row/column
        return undefined;
    }

    /** Handle cell is outer bound of top/bottom */
    private _handleCellFocusScroll(direction: KeyboardInput.ARROW_UP | KeyboardInput.ARROW_DOWN): void {

        if (this._focusCell) {
            // getSelectedElementsCount returns floored value - can't use displayedRows.length because last row may be truncated
            const displayedRowsCount = this.smartTable.scrollBar.getSelectedElementsCount();
            const allDisplayedRowsCount = this.smartTable.stickyTableRows.length + displayedRowsCount;
            const allDisplayedRows = [... this.smartTable.stickyTableRows, ... this.smartTable.displayedRows];

            const focusRowUid = this._focusCell.row.uid;

            // If currently focused cell is in first row and up required
            // we are on first displayed row
            const isScrollUpFirstDisplayedRow = this.smartTable.displayedRows[0]?.uid === focusRowUid && direction === KeyboardInput.ARROW_UP;
            // we are on global first row
            const isScrollUpFirstRow = allDisplayedRows[0]?.uid === focusRowUid && direction === KeyboardInput.ARROW_UP;

            // If currently focused cell is in last row and down required
            let scrollDownIncrement = 1;
            let isScrollDown = allDisplayedRows[allDisplayedRowsCount - 1]?.uid === focusRowUid && direction === KeyboardInput.ARROW_DOWN;
            if (allDisplayedRows.length > allDisplayedRowsCount &&
                allDisplayedRows[allDisplayedRows.length - 1]?.uid === focusRowUid && direction === KeyboardInput.ARROW_DOWN) {
                // focus cell is on the last row (and the row is truncated)
                isScrollDown = true;
                // we scroll down by 2 in order to have the focus cell fully displayed
                scrollDownIncrement = 2;
            }

            if (isScrollUpFirstDisplayedRow || isScrollUpFirstRow || isScrollDown) {

                // Get current index displayed in table
                let newSmartTableIndex = isScrollUpFirstRow ? 0 : this.smartTable.options.firstDisplayedItemIndex;
                if (isScrollUpFirstDisplayedRow || isScrollUpFirstRow) {
                    newSmartTableIndex--;
                    if (newSmartTableIndex < 0) {
                        if (isScrollUpFirstRow) {
                            // Handle outer bounds (index under first row index <=> 0) => move to bottom
                            newSmartTableIndex = this.smartTable.displayableRows.length - displayedRowsCount;
                        } else {
                            // We are inside sticky rows
                            newSmartTableIndex = 0;
                        }
                    }
                } else {// isScrollDown
                    newSmartTableIndex += scrollDownIncrement;

                    // Handle outer bounds (index upper last row index) => move to top
                    if (allDisplayedRowsCount === allDisplayedRows.length &&// if last row is fully visible
                        newSmartTableIndex + allDisplayedRowsCount >= this.smartTable.displayableRows.length) {// and we go beyond
                        newSmartTableIndex = 0;
                    }
                }
                this.smartTable.options.setAndRefreshIfChanged({ firstDisplayedItemIndex: newSmartTableIndex });
            }
        }
    }

    /** When a click is triggered on document, if activeElement isn't smartTable, remove focusCell */
    public onDocumentClickUpdateFocusCell: EventListenerObject = {
        handleEvent: () => {
            if (document.activeElement !== this.smartTable) {
                this.setFocusCell(undefined);
            }
        }
    };


    /** When a click is triggered on document, if edition is occurring and target isn't editor of editedCell, finish value edition */
    public onDocumentClickFinishCellEdition: EventListenerObject = {
        handleEvent: e => {
            // Check if an edition is occurring
            if (!this.isEditState()) { return; }

            const focusCell = this.getFocusCell();
            if (!focusCell) { return; }

            const target = e.target as HTMLElement | undefined;
            if (!target) { return; }

            // Check if click isn't in editedCell
            if (!findNearestMatch(target, elt => elt === focusCell.html)) {
                // Finish edition of cell
                this._finishValueEdition();
            }
        }
    };
}